/**
 * @file Builds the per-frame explosion rect list for the space scene's
 * draw list. Extracted from `spaceDrawBuilders.ts` to keep that file
 * under the AGENTS.md 300-line cap. Owns the explosion-frame palette
 * and the singleton burst-pool iteration.
 */

import {
  getExplosionPool,
  getExplosionFrameIndex,
  EXPLOSION_FRAME_WIDTH,
  EXPLOSION_FRAME_HEIGHT,
} from '../engine/explosionVfx';
import type { SpaceRect } from './spaceRender';

/**
 * Greybox palette for the 6 explosion frames. Hot yellow flash on
 * frame 0, peaks orange/red mid-burst, then cools to smoke grey on
 * frames 4-5. Module-private so the sprite-sheet upgrade only has to
 * delete this constant and route through the existing `spriteFrame`
 * field.
 *
 * DECISION: 6-tuple keyed by {@link getExplosionFrameIndex}'s output
 * (clamped 0..5) so callers do not need bounds checks. Length is tied
 * to `EXPLOSION_FRAME_COUNT` via the helper contract, not duplicated
 * as a constant here.
 */
const EXPLOSION_FRAME_PALETTE: readonly string[] = [
  '#fff5b3', // frame 0 — initial bright flash
  '#ffc14d', // frame 1 — saturating orange
  '#ff7a1f', // frame 2 — peak ember
  '#e63a1f', // frame 3 — deep red
  '#7a4030', // frame 4 — cooling smoke ember
  '#3a2a26', // frame 5 — fading smoke trail
] as const;

/**
 * Build the per-frame explosion rect list from the singleton burst
 * pool. Iterates the full pool (including inactive slots) and skips on
 * `!inst.active` so the loop is amortised O(EXPLOSION_POOL_SIZE)
 * regardless of how many bursts are live. The pool index doubles as
 * the React key — pool length is fixed and slots recycle in place, so
 * `explosion-<i>` is stable across frames.
 *
 * Each rect carries both a fallback {@link SpaceRect.color} (frame-
 * indexed palette ramp) and the matching {@link SpaceRect.spriteFrame}
 * so renderers prefer the explosion sheet when decoded.
 *
 * @param cameraX - Camera scroll offset for world→screen conversion.
 * @returns Camera-relative rects, one per active burst.
 */
export function buildExplosionRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  const pool = getExplosionPool();
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    if (!inst.active) continue;
    const frame = getExplosionFrameIndex(inst.elapsed);
    out.push({
      x: inst.x - cameraX - EXPLOSION_FRAME_WIDTH / 2,
      y: inst.y - EXPLOSION_FRAME_HEIGHT / 2,
      width: EXPLOSION_FRAME_WIDTH,
      height: EXPLOSION_FRAME_HEIGHT,
      color: EXPLOSION_FRAME_PALETTE[frame],
      spriteFrame: frame,
      key: `explosion-${i}`,
    });
  }
  return out;
}
